import {
  PlayerAnimationsNames,
  type InternalCrossfadeData
} from '@/app/types'
import type { Athlete } from './Athlete'

/**
 * Sprava internych crossfadeov
 */
export class InternalCrossfadesManager {

  /** Ci je dostupny interny crossfade */
  private active = false

  /** Info ohladom internych crossfadeov */
  private info: {[key in PlayerAnimationsNames]?: InternalCrossfadeData } = {
    [PlayerAnimationsNames.doublePoling]: {
      weightStart: 0,
      weightEnd: 0,
      frames: 0,
      actualFrame: 0
    },
    [PlayerAnimationsNames.downhill]: {
      weightStart: 0,
      weightEnd: 0,
      frames: 0,
      actualFrame: 0
    },
    [PlayerAnimationsNames.downhillLeft]: {
      weightStart: 0,
      weightEnd: 0,
      frames: 0,
      actualFrame: 0
    },
    [PlayerAnimationsNames.downhillRight]: {
      weightStart: 0,
      weightEnd: 0,
      frames: 0,
      actualFrame: 0
    },
    [PlayerAnimationsNames.glidePoling]: {
      weightStart: 0,
      weightEnd: 0,
      frames: 0,
      actualFrame: 0
    },
    [PlayerAnimationsNames.strideGlide]: {
      weightStart: 0,
      weightEnd: 0,
      frames: 0,
      actualFrame: 0
    },
  }

  /**
   * Konstruktor
   * @param athlete - Atlet
   * @param changePathAnimations - zoznam animacii na zmenu drahy
   */
  public constructor(private athlete: Athlete, private changePathAnimations: PlayerAnimationsNames[]) {}

  /**
   * Ci je aktivny
   * @returns True, ak je aktivny
   */
  public isActive(): boolean {

    return this.active

  }

  /**
   * Nastavenie opacneho procesu crossfadeu pre vsetky aktivne interne crossfadey (tj vahu chceme dat na hodnotu 0)
   * @param frames - Pocet frameov na tento proces
   * @param exceptAnimation - Ktora animacia je vynimka a nechceme, aby sa pridala do tohto procesu
   */
  private reverseAllActive(frames: number, exceptAnimation: PlayerAnimationsNames): void {

    for (const [key, value] of Object.entries(this.info)) {

      // iba aktivne a mimo vynimky
      const weight = this.athlete.animationsManager.getAnimationAction(key).weight
      if (key !== exceptAnimation && value.frames > 0 && weight > 0) {

        value.actualFrame = 0
        value.frames = frames
        value.weightStart = this.athlete.animationsManager.getAnimationAction(key).weight
        value.weightEnd = 0

      }

    }

  }

  /**
   * Nastavenie casu novej animacie pomocou casu starej animacie, pomerovo
   * @param animationNew - nova animacia
   * @param animationOld - stara animacia
   */
  private setNewAnimationTimeFromOldAnimation(
    animationNew: PlayerAnimationsNames,
    animationOld: PlayerAnimationsNames
  ): void {

    const timeOld = this.athlete.animationsManager.getAnimationAction(animationOld).time
    const durationOld = this.athlete.animationsManager.getDuration(animationOld)
    const durationNew = this.athlete.animationsManager.getDuration(animationNew)
    const percent = durationOld === 0 ? 0 : timeOld / durationOld
    const timeNew = durationNew * percent

    this.athlete.animationsManager.getAnimationAction(animationNew).time = timeNew

  }

  /**
   * Nastavenie interneho crossfadeu, kde chceme, aby sa weight dostala casom na hodnotu 1, ostatne robi reverse metoda
   * @param animation - Animacia
   * @param animationRunning - Aktualne beziaca animacia
   * @param frames - Pocet frameov na tento proces
   * @param changeTime - Ci sa ma nastavovat cas podla starej animacie, v podstate keepTime parameter crossfadeTo
   * @returns Nova beziaca animacia
   */
  public setCrossfade(
    animation: PlayerAnimationsNames,
    animationRunning: PlayerAnimationsNames,
    frames: number,
    changeTime = true
  ): PlayerAnimationsNames | undefined {

    // nastavime novy interny crossfade, ale iba ked tato animacia nie je posledna
    if (!this.active || animationRunning === animation) return undefined

    // interny crossfade animacie este bezi, takze bude treba prejst vsetky animacie a nastavit ich opacne
    this.reverseAllActive(frames, animation)

    this.info[animation] = {
      actualFrame: 0,
      frames: frames,
      weightStart: this.athlete.animationsManager.getAnimationAction(animation).weight,
      weightEnd: 1
    }

    // nastavme este cas animacie na rovnaky ako mala predosla animacia pomerovo - iba mimo tych na zmenu drah
    if (!this.changePathAnimations.includes(animation) && changeTime) {

      this.setNewAnimationTimeFromOldAnimation(animation, animationRunning)

    }

    return animation

  }

  /**
   * Sprava internych crossfadeov
   */
  public update(): void {

    if (!this.active) return

    for (const [key, value] of Object.entries(this.info)) {

      // iba aktivne
      if (value.actualFrame < value.frames && value.frames > 0) {

        value.actualFrame += 1
        const percent = value.actualFrame / value.frames

        // a teraz upravime vahu
        const newWeightRaw = value.weightStart + ((value.weightEnd - value.weightStart) * percent)
        const newWeight = Math.max(Math.min(newWeightRaw, 1), 0) // aplikujeme min a max
        this.athlete.animationsManager.setWeight(key, newWeight)

      }

    }

  }

  /**
   * Zapnutie internych crossfadeov
   */
  public enable(): void {

    this.active = true

  }

  /**
   * Vypnutie internych crossfadeov
   * @param animationRunning - Aktualne beziaca animacia
   */
  public disable(animationRunning: PlayerAnimationsNames): void {

    if (!this.active) return

    this.active = false

    // musime este poriesit vsetky vahy, aby sa zresetovali na spravne hodnoty
    for (const key of Object.keys(this.info)) {

      const weight = key === animationRunning ? 1 : 0
      this.athlete.animationsManager.setWeight(key, weight)

    }

    // este musime anim running dat changeTo, aby sa nastavilo aj interne v animacnom manageri
    this.athlete.animationsManager.changeTo(animationRunning)

  }

  /**
   * Zresetovanie
   */
  public reset(): void {

    this.active = false

    this.info = {
      [PlayerAnimationsNames.doublePoling]: {
        weightStart: 0,
        weightEnd: 0,
        frames: 0,
        actualFrame: 0
      },
      [PlayerAnimationsNames.downhill]: {
        weightStart: 0,
        weightEnd: 0,
        frames: 0,
        actualFrame: 0
      },
      [PlayerAnimationsNames.downhillLeft]: {
        weightStart: 0,
        weightEnd: 0,
        frames: 0,
        actualFrame: 0
      },
      [PlayerAnimationsNames.downhillRight]: {
        weightStart: 0,
        weightEnd: 0,
        frames: 0,
        actualFrame: 0
      },
      [PlayerAnimationsNames.glidePoling]: {
        weightStart: 0,
        weightEnd: 0,
        frames: 0,
        actualFrame: 0
      },
      [PlayerAnimationsNames.strideGlide]: {
        weightStart: 0,
        weightEnd: 0,
        frames: 0,
        actualFrame: 0
      },
    }

  }

}
